import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { Package } from "lucide-react";

export function CostBreakdownChart({ data_dish_performance }) {
  // Prepare data - take top 5 and group rest as "Others"
  const processedData = (() => {
    if (!data_dish_performance || data_dish_performance.length === 0) {
      return [];
    }

    const sorted = [...data_dish_performance].sort((a, b) => b.value - a.value);
    
    if (sorted.length <= 5) {
      return sorted;
    }

    const top5 = sorted.slice(0, 5);
    const others = sorted.slice(5);
    const othersTotal = others.reduce((sum, item) => sum + item.value, 0);

    return [
      ...top5,
      { name: "Others", value: othersTotal }
    ];
  })();

  // Calculate total
  const total = processedData.reduce((sum, item) => sum + item.value, 0);

  // Colors
  const COLORS = [
    "hsl(var(--primary))",
    "hsl(var(--warning))",
    "hsl(var(--success))",
    "hsl(var(--destructive))",
    "hsl(var(--chart-3))",
    "hsl(var(--muted-foreground))",
  ];

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || payload.length === 0) return null;

    const data = payload[0].payload;
    const percentage = total > 0 ? ((data.value / total) * 100).toFixed(1) : 0;

    return (
      <div className="rounded-lg border border-border bg-card/95 backdrop-blur-sm p-3 shadow-lg">
        <p className="font-semibold text-foreground mb-1">{data.name}</p>
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">
            Amount: <span className="font-semibold text-foreground">₹{data.value.toLocaleString("en-IN")}</span>
          </p>
          <p className="text-sm text-muted-foreground">
            Share: <span className="font-semibold text-primary">{percentage}%</span>
          </p>
        </div>
      </div>
    );
  };

  // Custom label
  const renderCustomLabel = (entry) => {
    const percentage = total > 0 ? ((entry.value / total) * 100).toFixed(0) : 0;
    if (percentage < 5) return ""; // Don't show label for small slices
    return `${percentage}%`;
  };

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-semibold text-foreground text-lg">
              Purchase Distribution
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Top ingredients by cost
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-muted-foreground">Total</p>
            <p className="text-lg font-bold text-foreground">
              ₹{total.toLocaleString("en-IN")}
            </p>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="p-6">
        {processedData.length > 0 ? (
          <>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={processedData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomLabel}
                    outerRadius={100}
                    innerRadius={60}
                    fill="#8884d8"
                    dataKey="value"
                    animationDuration={1000}
                  >
                    {processedData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Legend */}
            <div className="mt-6 space-y-2 max-h-40 overflow-y-auto">
              {processedData.map((item, index) => {
                const percentage = total > 0 ? ((item.value / total) * 100).toFixed(1) : 0;
                return (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <span className="text-sm font-medium text-foreground truncate">
                        {item.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-muted-foreground">
                        {percentage}%
                      </span>
                      <span className="text-sm font-semibold text-foreground min-w-[80px] text-right">
                        ₹{item.value.toLocaleString("en-IN")}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="h-[280px] flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No purchase data available</p>
              <p className="text-sm mt-1">Data will appear after purchases</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}